import { Page } from 'components/Page/Page';
import { Button, Tab, Tabs } from 'react-bootstrap';
import { Redirect } from 'react-router-dom';
import {
  ChannelGroupFormModelData,
  ChannelGroupTabs
} from './ChannelGroupFormModel';
import i18n from 'i18n';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import styles from './channelGroupForm.module.scss';
import { useCallback, useEffect, useRef } from 'react';
import { Form } from 'components/common/form/Form';
import { Formik, FormikProps, useFormikContext, getIn } from 'formik';
import {
  ChannelGroupFormData,
  ChannelGroupListDTO,
  ChannelType
} from 'core/channelGroup/ChannelGroup';
import { ChannelGroupBasicForm } from './ChannelGroupBasicForm';
import { ChannelGroupReportForm } from './ChannelGroupReportForm';
import { ChannelGroupBidPriceForm } from './ChannelGroupBidPriceForm';
import { ChannelGroupSpecialSettingsForm } from './SpecialSettingsForm';
import { ChannelGroupAdTypeList } from '../ChannelGroupAdTypeList/ChannelGroupAdTypeList';
import { ChannelGroupOutdoorCreativeSizeForm } from './ChannelGroupOutdoorCreativeSizeForm';
import { ChannelGroupNonExpressPassCategoriesForm } from './ChannelGroupNonExpressPassCategoriesForm';
import { ChannelGroupEpaperDefaultImageForm } from './ChannelGroupEpaperDefaultImageForm';
import { ErrorHandler } from 'components/common/form/FormErrorHandler/FormErrorHandler';
import { validateMinMax } from 'utils/ValidateUtils';

const SubmitErrorTabSwitch: React.FC<{
  tab: ChannelGroupTabs;
  onTabChange: (tab: ChannelGroupTabs) => void;
}> = ({ tab, onTabChange }) => {
  const { submitCount, errors } = useFormikContext<ChannelGroupFormData>();
  const prevSubmitCountRef = useRef(0);

  useEffect(() => {
    if (submitCount > prevSubmitCountRef.current) {
      prevSubmitCountRef.current = submitCount;
      if (
        getIn(errors, 'property.addonProps.defaultMargin') &&
        tab !== ChannelGroupTabs.REPORT
      ) {
        onTabChange(ChannelGroupTabs.REPORT);
      }
      if (
        (errors.nameI18n || errors.alertEmails) &&
        tab !== ChannelGroupTabs.BASIC
      ) {
        onTabChange(ChannelGroupTabs.BASIC);
      }
      // ECOPD-5581: 預設圖片是電子紙渠道的必填，但它在自己的 tab 上。
      // 不跳過去的話，使用者按了送出只會看到「沒反應」。
      if (
        getIn(errors, 'epaperDefaultImageData') &&
        tab !== ChannelGroupTabs.EPAPER_DEFAULT_IMAGE
      ) {
        onTabChange(ChannelGroupTabs.EPAPER_DEFAULT_IMAGE);
      }
    }
  }, [submitCount, errors, tab, onTabChange]);

  return null;
};

type ChannelGroupDetailProps = {
  channelGroups: ChannelGroupListDTO[];
  useModel: (
    channelGroups: ChannelGroupListDTO[],
    onChannelGroupChanged?: () => void
  ) => ChannelGroupFormModelData;
  /** ECOPD-5572：存檔成功後通知外層重抓渠道清單。 */
  onChannelGroupChanged?: () => void;
};

export const ChannelGroupForm: React.FC<ChannelGroupDetailProps> = ({
  channelGroups,
  useModel,
  onChannelGroupChanged
}) => {
  const {
    loading,
    title,
    isEditing,
    tab,
    redirectPathData,
    agencyOptions,
    currencyOptions,
    tenmaxCategoryOptions,
    channelGroupId,
    initChannelGroup,
    breadcrumbsRoutes,
    onTabChange,
    submit,
    cancel
  } = useModel(channelGroups, onChannelGroupChanged);

  const formContentDom = useRef(null);

  const renderBtns = useCallback(
    () => (
      <>
        <Button variant='primary' size='sm' type='submit'>
          {i18n.t('common.buttons.submit')}
        </Button>
        <Button variant='secondary' size='sm' onClick={cancel}>
          {i18n.t('common.buttons.cancel')}
        </Button>
      </>
    ),
    [cancel]
  );

  const validate = useCallback((values: ChannelGroupFormData) => {
    const defaultMarginError = validateMinMax(
      values.property.addonProps.defaultMargin,
      0,
      1
    );
    if (defaultMarginError) {
      return {
        property: { addonProps: { defaultMargin: defaultMarginError } }
      };
    }
  }, []);

  const renderForm = useCallback(
    (formikProps: FormikProps<ChannelGroupFormData>) => {
      return (
        <div ref={formContentDom}>
          <SubmitErrorTabSwitch tab={tab} onTabChange={onTabChange} />
          <ErrorHandler
            parentRef={formContentDom}
            isSubmitting={formikProps.isSubmitting}
            submitCount={formikProps.submitCount}
          />
          <Form
            formikProps={formikProps}
            renderFormBtns={renderBtns}
            withNavigationPrompt
            noValidate
          >
            <Tabs
              id='reportDownloadPanelTab'
              defaultActiveKey={ChannelGroupTabs.BASIC}
              activeKey={tab}
              onSelect={onTabChange}
            >
              <Tab
                eventKey={ChannelGroupTabs.BASIC}
                title={i18n.t('channelGroups.titles.basicTab')}
              >
                <ChannelGroupBasicForm channelGroupId={channelGroupId} />
              </Tab>
              <Tab
                eventKey={ChannelGroupTabs.REPORT}
                title={i18n.t('channelGroups.titles.reportTab')}
              >
                <ChannelGroupReportForm
                  isEditing={isEditing}
                  agencyOptions={agencyOptions}
                  currencyOptions={currencyOptions}
                />
              </Tab>
              <Tab
                eventKey={ChannelGroupTabs.AD_TYPES}
                title={i18n.t('channelGroups.titles.adTypesTab')}
              >
                <ChannelGroupAdTypeList />
              </Tab>
              <Tab
                eventKey={ChannelGroupTabs.BID_PRICE}
                title={i18n.t('channelGroups.titles.bidPriceTab')}
              >
                <ChannelGroupBidPriceForm />
              </Tab>
              {initChannelGroup?.channelType !== ChannelType.BEACON && (
                <Tab
                  eventKey={ChannelGroupTabs.SPECIAL_SETTINGS}
                  title={i18n.t('channelGroups.titles.specialSettingsTab')}
                >
                  <ChannelGroupSpecialSettingsForm />
                </Tab>
              )}
              {initChannelGroup?.channelType !== ChannelType.BEACON && (
                <Tab
                  eventKey={ChannelGroupTabs.OUTDOOR_CREATIVE_SIZES}
                  title={i18n.t('channelGroups.titles.outdoorCreativeSizesTab')}
                >
                  <ChannelGroupOutdoorCreativeSizeForm />
                </Tab>
              )}
              {initChannelGroup?.channelType !== ChannelType.BEACON && (
                <Tab
                  eventKey={ChannelGroupTabs.NON_EXPRESS_PASS_CATEGORIES}
                  title={i18n.t(
                    'channelGroups.titles.nonExpressPassCategoriesTab'
                  )}
                >
                  <ChannelGroupNonExpressPassCategoriesForm
                    tenmaxCategoryOptions={tenmaxCategoryOptions}
                  />
                </Tab>
              )}
              {initChannelGroup?.channelType === ChannelType.E_PAPER && (
                <Tab
                  eventKey={ChannelGroupTabs.EPAPER_DEFAULT_IMAGE}
                  title={i18n.t('channelGroups.titles.epaperDefaultImageTab')}
                >
                  <ChannelGroupEpaperDefaultImageForm />
                </Tab>
              )}
            </Tabs>
          </Form>
        </div>
      );
    },
    [
      tab,
      isEditing,
      agencyOptions,
      currencyOptions,
      tenmaxCategoryOptions,
      channelGroupId,
      initChannelGroup?.channelType,
      onTabChange,
      renderBtns
    ]
  );

  if (redirectPathData) {
    return <Redirect to={redirectPathData} />;
  }

  if (!initChannelGroup) {
    return <LoadingIndicator />;
  }

  return (
    <div style={{ height: '100%' }}>
      {loading && <LoadingIndicator />}
      <Page
        title={title}
        breadcrumbsRoutes={breadcrumbsRoutes}
        spaceBetweenTopAreaAndContent={false}
        topAreaEndWithShadow
      >
        <div className={styles.channelGroupForm}>
          <Formik
            initialValues={initChannelGroup}
            onSubmit={submit}
            validate={validate}
            validateOnBlur={false}
          >
            {renderForm}
          </Formik>
        </div>
      </Page>
    </div>
  );
};
